import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosGet, axiosPatch, axiosPost, axiosPut } from 'Redux/axiosHelper'
import {
  AttachDropInTokid,
  CreatDropInAttendeeIntent,
  CreateDropInAttendeePayload,
  DropInAttendee,
  DropOutAttendeePayload,
  GetDropInAttendeeDetails,
  GetFamilyMembersParams,
} from 'types/event/dropInAttendees'
import { AttendeeStatusResp } from 'types/event/eventAttendance'
import { Person } from 'types/globalTypes'

// get event dropins
const getEventDropInList = async (eventId: string, date: string) => {
  // const response = await axiosGet(`v1/dropin/past-list/${eventId}/${date}`)
  const response = await axiosGet(`v1/dropin/list/${eventId}`, { today: date })
  return response.data
}

export const useGetEventDropInList = (eventId: string, date: string) => {
  const eventDropInList = useQuery<DropInAttendee[]>(
    ['event-dropin-list'],
    () => getEventDropInList(eventId, date),
    { enabled: false },
  )
  return eventDropInList
}

// create drop-in attendee and check-in
const createDropInAttendee = async (payload: CreateDropInAttendeePayload) => {
  const response = await axiosPost(
    payload,
    `v2/check/admin/create-dropin-attendee`,
  )
  return response.data
}

export const useCreateDropInAttendee = () => {
  const queryClient = useQueryClient()
  return useMutation(createDropInAttendee, {})
}

// edit drop-in attendee and check-in
const editDropInAttendee = async ({
  attendeeId,
  payload,
}: {
  attendeeId: string
  payload: CreateDropInAttendeePayload
}) => {
  const response = await axiosPatch(
    payload,
    `v2/check/admin/dropin-attendee/${attendeeId}`,
  )
  return response.data
}
export const useEditDropInAttendee = () => {
  const queryClient = useQueryClient()
  return useMutation(editDropInAttendee, {
    onSuccess: data => {
      queryClient.setQueriesData(
        ['drop-in-attendee-details'],
        (oldData: any) => {
          if (!oldData) {
            return {
              checkIns: [{ dropInAttendee: data }],
              cost: [
                { amount: 1, price: 1, unit: 'minute', name: 'Drop-in ticket' },
              ],
            }
          }

          return {
            ...oldData,
            checkIns: [
              {
                ...oldData.checkIns[0],
                dropInAttendee: {
                  ...oldData.checkIns[0].dropInAttendee,
                  ...data,
                },
              },
              ...oldData.checkIns.slice(1),
            ],
          }
        },
      )
    },
  })
}

// get drop-in attendee details
const getDropInAttendeeDetails = async (
  params: GetDropInAttendeeDetails,
) => {
  const response = await axiosGet(
    `v1/dropin/recent-check-in`,
    params,
  )
  return response.data
}

export const useGetDropInAttendeeDetails = (
  params: GetDropInAttendeeDetails,
) => {
  const dropInAttendeeDetail = useQuery<any>(
    ['drop-in-attendee-details'],
    () => getDropInAttendeeDetails(params),
    { enabled: false },
  )
  return dropInAttendeeDetail
}

// get family members by email
const getFamilyMembersByEmail = async (params: GetFamilyMembersParams) => {
  const response = await axiosGet(
    `v1/dropin/get-members-by-email/${params.eventId}`,
    params,
  )
  return response.data
}
export const useGetFamilyMembersByEmail = (params: GetFamilyMembersParams) => {
  const familyMembersByEmail = useQuery<any>(
    ['get-family-members-by-email'],
    () => (params.email ? getFamilyMembersByEmail(params) : null),
  )
  return familyMembersByEmail
}

// create intent and drop-out attendee
const createIntentAndDropOutAttendee = async (
  payload: CreatDropInAttendeeIntent,
) => {
  const response = await axiosPost(payload, `v2/check/admin/charge-dropin`)
  return response.data
}

export const useCreateIntentAndDropOutAttendee = () => {
  const queryClient = useQueryClient()
  return useMutation(createIntentAndDropOutAttendee, {})
}

// add drop-in attendee to family
const addDropInAttendeeToFamily = async (payload: AttachDropInTokid) => {
  const response = await axiosPut(payload, `v1/dropin/add-to-family`)
  return response.data
}

export const useAddDropInAttendeeToFamily = () => {
  return useMutation(addDropInAttendeeToFamily)
}

// drop out from event
const dropOutFromEvent = async (payload: DropOutAttendeePayload) => {
  const response = await axiosPost(payload, `v2/check/admin/drop-out`)
  return response.data
}
export const useDropOutFromEvnet = () => {
  return useMutation(dropOutFromEvent)
}

// drop-in in the event
const dropInEvent = async (payload: any) => {
  const response = await axiosPost(payload, `v2/check/admin/drop-in`)
  return response.data
}

export const useDropInEvents = () => {
  return useMutation(dropInEvent)
}
